import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { LayoutList, TrendingUp } from "lucide-react";

export const PositionManagement = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Position Management</h1>
        
        <div className="text-sm text-muted-foreground mb-8">
          <p>Learn how to monitor, manage, and close your open positions</p>
        </div>

        {/* Viewing Positions */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Viewing Your Positions</h2>
          
          <p className="text-muted-foreground mb-6">
            All your open positions are displayed in the Positions panel. If you don't see this panel, 
            click the "+" button in the header and select "Positions" to add it to your layout.
          </p>

          <div className="p-6 border border-border rounded-lg bg-card mb-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <LayoutList className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-lg">Position Information</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Each position row displays the following information:
            </p>
            <ul className="text-sm text-muted-foreground space-y-2 pl-4 list-disc">
              <li><strong className="text-foreground">Market:</strong> Which asset you're trading (e.g., BTC-USD)</li>
              <li><strong className="text-foreground">Side:</strong> Long or Short position</li>
              <li><strong className="text-foreground">Size:</strong> Total position value in USD</li>
              <li><strong className="text-foreground">Entry Price:</strong> Price where you opened the position</li>
              <li><strong className="text-foreground">Current Price:</strong> Live market price (updates in real-time)</li>
              <li><strong className="text-foreground">Leverage:</strong> Leverage level used for this position</li>
              <li><strong className="text-foreground">Unrealized P&L:</strong> Current profit or loss (green for profit, red for loss)</li>
              <li><strong className="text-foreground">Liquidation Price:</strong> Price at which your position will be liquidated</li>
            </ul>
          </div>
        </section>

        {/* Monitoring P&L */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Monitoring Profit & Loss</h2>
          
          <div className="grid gap-4 md:grid-cols-2 mb-6">
            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <TrendingUp className="h-6 w-6 text-emerald-500" />
                </div>
                <h3 className="font-semibold text-lg">Unrealized P&L</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                Shows your current profit or loss while the position is still open. This value changes 
                in real-time as the market price moves.
              </p>
              <div className="p-3 bg-muted/30 rounded-lg text-sm">
                <strong>Formula:</strong><br />
                Long: (Current Price - Entry Price) / Entry Price × Position Size<br />
                Short: (Entry Price - Current Price) / Entry Price × Position Size
              </div>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <TrendingUp className="h-6 w-6 text-blue-500" />
                </div>
                <h3 className="font-semibold text-lg">Realized P&L</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                The actual profit or loss locked in when you close a position. This is added to (or 
                subtracted from) your account balance.
              </p>
              <div className="p-3 bg-muted/30 rounded-lg text-sm">
                Unrealized P&L becomes Realized P&L when you close the position.
              </div>
            </div>
          </div>

          <div className="p-6 border border-border rounded-lg bg-card">
            <h3 className="font-semibold mb-4">P&L Calculation Example:</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Position: Long BTC</span>
                <span className="font-semibold">$10,000 size</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Entry Price:</span>
                <span className="font-semibold">$50,000</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Current Price:</span>
                <span className="font-semibold">$52,000</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <span className="font-semibold">Unrealized P&L:</span>
                <span className="font-bold text-emerald-500">+$400</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Calculation: ($52,000 - $50,000) / $50,000 × $10,000 = $400 profit (4%)
            </p>
          </div>
        </section>

        {/* Closing Positions */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Closing Positions</h2>
          
          <p className="text-muted-foreground mb-6">
            When you're ready to exit a position and lock in your profit or loss, you can close it 
            with a single click.
          </p>

          <div className="space-y-4 mb-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                1
              </div>
              <div>
                <h3 className="font-semibold mb-1">Locate Your Position</h3>
                <p className="text-sm text-muted-foreground">
                  Find the position you want to close in the Positions panel.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                2
              </div>
              <div>
                <h3 className="font-semibold mb-1">Click the Close Button</h3>
                <p className="text-sm text-muted-foreground">
                  On the right side of the position row, click the "✕ Close" button.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                3
              </div>
              <div>
                <h3 className="font-semibold mb-1">Confirm Closure</h3>
                <p className="text-sm text-muted-foreground">
                  Confirm that you want to close the position. The position will be closed at the 
                  current market price.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                4
              </div>
              <div>
                <h3 className="font-semibold mb-1">P&L Realized</h3>
                <p className="text-sm text-muted-foreground">
                  Your realized P&L is immediately added to or subtracted from your account balance. 
                  The used margin from this position becomes available balance.
                </p>
              </div>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            The "Close" button closes 100% of the selected position at the current market price. 
            You can also use partial Take Profit or Stop Loss targets to automatically close portions 
            of your position at specific price levels (see below).
          </p>
        </section>

        {/* Take Profit & Stop Loss */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Take Profit & Stop Loss</h2>
          
          <p className="text-muted-foreground mb-6">
            You can set Take Profit (TP) and Stop Loss (SL) on any open position to automatically 
            close it when the price reaches your target.
          </p>

          <h3 className="text-lg font-semibold mb-4">How to Set TP/SL</h3>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-6">
            <li><strong className="text-foreground">From the Positions panel</strong> -- click the pencil icon in the TP/SL column of any open position to open the TP/SL editor</li>
            <li><strong className="text-foreground">When placing an order</strong> -- enable the "Take Profit / Stop Loss" checkbox in the order form before submitting</li>
            <li><strong className="text-foreground">From the chart</strong> -- drag the TP or SL lines directly on the chart to adjust prices visually</li>
          </ul>

          <h3 className="text-lg font-semibold mb-4">Standard TP/SL</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Set a single take-profit price and/or stop-loss price. You can enter a specific price 
            or use the ROI slider to set targets based on percentage return. The expected profit or 
            loss is displayed before you confirm.
          </p>

          <h3 className="text-lg font-semibold mb-4">Partial TP/SL</h3>
          <p className="text-sm text-muted-foreground mb-4">
            For more advanced management, switch to the "Partial" tab in the TP/SL editor. This lets 
            you set multiple take-profit and stop-loss levels at different prices, each closing a 
            portion of your position.
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            For example, you could set TP1 to close 50% of your position at +10% ROI, and TP2 to 
            close the remaining 50% at +20% ROI. Each target specifies a trigger price and close 
            amount (by percentage or absolute USD value). Partial targets appear as labeled lines on 
            the chart (TP1, TP2, SL1, etc.).
          </p>
        </section>

        {/* Editing Leverage & Margin */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Editing Leverage & Margin</h2>
          
          <p className="text-muted-foreground mb-6">
            You can adjust leverage and margin on open positions without closing them:
          </p>

          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>
              <strong className="text-foreground">Change leverage</strong> -- click the pencil icon next to the leverage value in the Positions panel. 
              The dialog shows your current leverage, the new leverage, and the margin impact (additional margin required or margin released).
            </li>
            <li>
              <strong className="text-foreground">Add or remove margin</strong> -- adjust the margin allocated to a specific position. 
              Adding margin reduces liquidation risk by moving the liquidation price further from the current price.
            </li>
          </ul>
        </section>

        {/* Managing Multiple Positions */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Managing Multiple Positions</h2>
          
          <p className="text-muted-foreground mb-6">
            You can hold multiple positions across different markets simultaneously. Here's how to 
            manage them effectively:
          </p>

          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Account View</h3>
              <p className="text-sm text-muted-foreground">
                Navigate to the Account page (via the header menu) to see a comprehensive view of 
                all your positions, history, and account metrics in one place.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Position Sorting</h3>
              <p className="text-sm text-muted-foreground">
                Positions are typically displayed with the most recent at the top. You can quickly 
                scan through your open positions to check their status.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Individual Management</h3>
              <p className="text-sm text-muted-foreground">
                Each position can be managed independently. You can close one position while keeping 
                others open, or close all positions if needed.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Total Unrealized P&L</h3>
              <p className="text-sm text-muted-foreground">
                The Balances panel shows your total unrealized P&L across all positions, giving you 
                a quick snapshot of your overall performance.
              </p>
            </div>
          </div>
        </section>

        {/* Position Monitoring Best Practices */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Position Monitoring Best Practices</h2>
          
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>
              <strong className="text-foreground">Regular check-ins</strong> -- even if holding positions long-term, check them at least daily to monitor 
              P&L, funding rates, and liquidation risk.
            </li>
            <li>
              <strong className="text-foreground">Watch liquidation prices</strong> -- always be aware of your liquidation price for each position. If the market price 
              starts approaching it, consider adding collateral or closing the position.
            </li>
            <li>
              <strong className="text-foreground">Set exit plans</strong> -- before opening a position, decide on your profit target and maximum acceptable loss. 
              Stick to these levels to avoid emotional decision-making.
            </li>
            <li>
              <strong className="text-foreground">Track your performance</strong> -- review your closed positions in the Account page to analyze what worked and what 
              didn't. Learn from both profitable and losing trades.
            </li>
            <li>
              <strong className="text-foreground">Don't overtrade</strong> -- having too many positions can make portfolio management difficult and increase risk. 
              Focus on quality positions rather than quantity.
            </li>
          </ul>
        </section>

        {/* Common Issues */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Common Position Management Issues</h2>
          
          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Can't Close Position</h3>
              <p className="text-sm text-muted-foreground">
                If you're unable to close a position, check your internet connection and ensure the 
                market is operating normally. Try refreshing the page. If the issue persists, contact support.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">P&L Not Updating</h3>
              <p className="text-sm text-muted-foreground">
                P&L updates in real-time based on live price feeds. If it's not updating, check that 
                your connection status shows "Online" in the bottom status bar. Prices come from professional data providers 
                WebSocket feeds.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Position Liquidated Unexpectedly</h3>
              <p className="text-sm text-muted-foreground">
                If a position was liquidated, it means your margin ratio fell to 100% or below. This can happen 
                during volatile price movements. Use lower leverage and monitor your margin ratio to 
                prevent future liquidations.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Negative P&L Larger Than Expected</h3>
              <p className="text-sm text-muted-foreground">
                Remember that leverage amplifies losses as much as gains. A small percentage move against 
                your position with high leverage can result in large losses. Also factor in trading fees 
                and funding payments.
              </p>
            </div>
          </div>
        </section>

        <DocsNavigation 
          previous={{ title: "Order Types", href: "/order-types" }}
          next={{ title: "Available Markets", href: "/markets" }}
        />
      </div>
    </DocsLayout>
  );
};

